import { takeLatest, put, call, select } from 'redux-saga/effects';
import { Map } from 'immutable';

import * as recipesApi from '../../service/api/recipes';
import * as recipeActions from './action';
import * as devicesActions from '../device/action';
import toast from '../../utils/toast';
import { createUserFriendlyErrorMessage } from 'src/utils/utils';
import * as authSelector from '../authentication/selector';

export function * recipeRoot () {
  yield takeLatest(recipeActions.FETCH_RECIPES, fetchRecipes);
  yield takeLatest(recipeActions.UPDATE_RECIPE, updateRecipe);
  yield takeLatest(recipeActions.ADD_RECIPE, addRecipe);
  yield takeLatest(recipeActions.DELETE_RECIPE, deleteRecipe);
  yield takeLatest(recipeActions.ADD_RECIPE_CONDITION, addRecipeCondition);
  yield takeLatest(recipeActions.UPDATE_RECIPE_CONDITION, updateRecipeCondition);
  yield takeLatest(recipeActions.FETCH_RECIPE_BY_ID, fetchRecipeById);
  yield takeLatest(recipeActions.FETCH_DEVICE_SETTING_MAP, fetchDeviceSettingMap);
  yield takeLatest(recipeActions.CONFIRM_RECIPE_UPDATE, handleConfirmRecipeUpdate);
  yield takeLatest(recipeActions.CANCEL_RECIPE_UPDATE, handleCancelRecipeUpdate);
}

function * fetchRecipes (action) {
  try {
    const recipes = yield call(recipesApi.getRecipes, action);

    yield put(recipeActions.fetchRecipesSuccess(recipes || Map()));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error retrieving recipes'));
    yield put(recipeActions.fetchRecipesFailed(Map(err)));
  }
}
function * fetchDeviceSettingMap (action) {
  try {
    const settingsMap = yield call(recipesApi.getDeviceSettingMap, action);
    yield put(recipeActions.fetchDeviceSettingMapSuccess(settingsMap));
  } catch (err) {
    yield put(recipeActions.fetchDeviceSettingMapFailed(Map(err)));
  }
}

function * fetchRecipeById (action) {
  try {
    const recipe = yield call(recipesApi.getRecipeById, action);

    yield put(recipeActions.fetchRecipeByIdSuccess(recipe || Map()));
  } catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Error retrieving recipe By Id'), err.response.status);
    yield put(recipeActions.fetchRecipeByIdFailed(Map(err)));
  }
}

function * addRecipe (action) {
  try {
    const recipe = yield call(recipesApi.addRecipe, action);

    yield put(recipeActions.addRecipeSuccess(recipe));
    yield put(devicesActions.setSettingsModalClose(null));
  }
  catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Failed to create new Recipe'), err.response.status);
    yield put(recipeActions.addRecipeFailed(err));
  }
}

function * deleteRecipe (action) {
  try {
    yield call(recipesApi.deleteRecipe, action);
    yield put(recipeActions.deleteRecipeSuccess(action.recipeId));
  }
  catch (err: any) {
    toast.error(createUserFriendlyErrorMessage(err, 'Failed to delete recipe'), err.response.status);
    yield put(recipeActions.deleteRecipeFailed(err));
  }
}

function * updateRecipe (action) {
  try {
    const recipes = yield call(recipesApi.updateRecipe, action);
    yield put(recipeActions.updateRecipeSuccess(recipes));
  } catch (err: any) {
    const errorData = err.response?.data || err.response || {};
    // Check for pending batches warning
    if (errorData.code === 'VALIDATION_ERROR' && errorData.message === 'RECIPE_HAS_PENDING_BATCHES') {
      // Convert action to plain object for storage
      const actionData = {
        recipeId: action.recipeId,
        recipe: action.recipe
      };
      yield put(recipeActions.showRecipeUpdateWarning(errorData.pendingBatches || [], actionData, 'UPDATE_RECIPE'));
    } else {
      toast.error(createUserFriendlyErrorMessage(err, 'Error updating recipe'), err.response?.status);
      yield put(recipeActions.updateRecipeFailed(Map(err)));
    }
  }
}

function * addRecipeCondition (action) {
  try {
    const recipe = yield call(recipesApi.addRecipeCondition, action);
    yield put(recipeActions.addRecipeConditionSuccess(recipe));
  }
  catch (err: any) {
    const errorData = err.response?.data || err.response || {};
    if (errorData.code === 'VALIDATION_ERROR' && errorData.message === 'RECIPE_HAS_PENDING_BATCHES') {
      const actionData = {
        recipeId: action.recipeId,
        recipeCondition: action.recipeCondition
      };
      yield put(recipeActions.showRecipeUpdateWarning(errorData.pendingBatches || [], actionData, 'ADD_RECIPE_CONDITION'));
    } else {
      toast.error(createUserFriendlyErrorMessage(err, 'Failed to create new recipe condition'), err.response?.status);
      yield put(recipeActions.addRecipeConditionFailed(err));
    }
  }
}

function * updateRecipeCondition (action) {
  try {
    const recipe = yield call(recipesApi.updateRecipeCondition, action);

    yield put(recipeActions.updateRecipeConditionSuccess(recipe));
  } catch (err: any) {
    const errorData = err.response?.data || err.response || {};
    // Check for pending batches warning
    if (errorData.code === 'VALIDATION_ERROR' && errorData.message === 'RECIPE_HAS_PENDING_BATCHES') {
      // Convert action to plain object for storage
      const actionData = {
        recipeId: action.recipeId,
        recipeCondition: action.recipeCondition
      };
      yield put(recipeActions.showRecipeUpdateWarning(errorData.pendingBatches || [], actionData, 'UPDATE_RECIPE_CONDITION'));
    } else {
      toast.error(createUserFriendlyErrorMessage(err, 'Error updating recipe'), err.response?.status);
      yield put(recipeActions.updateRecipeConditionFailed(Map(err)));
    }
  }
}

function * handleConfirmRecipeUpdate () {
  const warningState = yield select((state) => state.recipe.getIn(['modal', 'updateWarning']));
  if (!warningState) {
    return;
  }

  // Double-check that the modal is still open and not cancelled
  const isOpen = warningState.get('open');
  if (!isOpen) {
    return;
  }

  const originalAction = warningState.get('originalAction');
  const actionType = warningState.get('actionType');

  // Ensure we have the necessary data before retrying
  if (!originalAction || !actionType) {
    return;
  }

  const originalActionData = originalAction.toJS ? originalAction.toJS() : originalAction;
  const recipeId = originalActionData.recipeId;

  // Get currentClientId from state - needed for API request to include X-Fox-Client-Id header
  const currentClientId = yield select(authSelector.getClientId);

  try {
    // Retry the original action with force flag
    if (actionType === 'UPDATE_RECIPE') {
      const recipe = originalActionData.recipe;
      // Ensure forceUpdate is set - handle both Immutable and plain objects
      let recipeWithForce;
      if (recipe && (typeof recipe.get === 'function' || typeof recipe.set === 'function')) {
        // It's an Immutable object - set forceUpdate and keep it as Immutable
        recipeWithForce = recipe.set('forceUpdate', true);
      } else {
        // It's a plain object - add forceUpdate
        recipeWithForce = { ...recipe, forceUpdate: true };
      }

      // Wait for the update to complete
      const updatedRecipe = yield call(recipesApi.updateRecipe, {
        recipeId,
        recipe: recipeWithForce,
        currentClientId
      });

      // Update the store with the successful result
      yield put(recipeActions.updateRecipeSuccess(updatedRecipe));

      // Refresh the recipe to ensure we have the latest data from the server
      yield put(recipeActions.fetchRecipeById(recipeId));

      // Clear the modal state after successful update
      yield put(recipeActions.cancelRecipeUpdate(recipeId));
    } else if (actionType === 'UPDATE_RECIPE_CONDITION' || actionType === 'ADD_RECIPE_CONDITION') {
      const recipeCondition = originalActionData.recipeCondition;
      // Ensure forceUpdate is set - handle both Immutable and plain objects
      let conditionWithForce;
      if (recipeCondition && (typeof recipeCondition.get === 'function' || typeof recipeCondition.set === 'function')) {
        // It's an Immutable object - set forceUpdate and keep it as Immutable
        conditionWithForce = recipeCondition.set('forceUpdate', true);
      } else {
        // It's a plain object - add forceUpdate
        conditionWithForce = { ...recipeCondition, forceUpdate: true };
      }

      const apiCall = actionType === 'ADD_RECIPE_CONDITION'
        ? recipesApi.addRecipeCondition
        : recipesApi.updateRecipeCondition;

      // Wait for the add/update to complete
      const updatedRecipe = yield call(apiCall, {
        recipeId,
        recipeCondition: conditionWithForce,
        currentClientId
      });

      // Update the store with the successful result
      yield put(recipeActions.updateRecipeConditionSuccess(updatedRecipe));

      // Refresh the recipe to ensure we have the latest data from the server
      yield put(recipeActions.fetchRecipeById(recipeId));

      // Clear the modal state after successful update
      yield put(recipeActions.cancelRecipeUpdate(recipeId));
    }
  } catch (err: any) {
    // If the update fails after confirmation, show an error
    const errorData = err.response?.data || err.response || {};
    const errorCode = errorData.code || err.code;
    const errorStatus = err.response?.status || err.status;

    // Handle permission errors specifically
    if (errorCode === 'PERMISSION_ERROR' || errorStatus === 403) {
      toast.error(
        'You do not have permission to update this recipe. Please contact your administrator if you believe this is an error.',
        errorStatus
      );
    } else {
      toast.error(createUserFriendlyErrorMessage(err, 'Error updating recipe'), errorStatus);
    }

    yield put(recipeActions.updateRecipeFailed(Map(err)));
    // Don't clear the modal state on error so user can try again
    return;
  }
}

function * handleCancelRecipeUpdate (action) {
  // Refresh the recipe data to ensure components get the correct state
  // This prevents components from keeping stale local state that might trigger re-submission
  if (action.recipeId) {
    // Fetch the latest recipe data to ensure UI is in sync with server state
    yield put(recipeActions.fetchRecipeById(action.recipeId));
  }

  // The reducer will handle closing the modal and clearing state
  // This handler ensures that canceling prevents any retry of the original action
  // and refreshes the recipe data so components can revert their local state
}

export default recipeRoot;
